import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Lock, RefreshCw, ServerCrash, WifiOff } from 'lucide-react';
import { ApiError } from '@/api/client';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/* ============================================================================================
   Loading
   ============================================================================================ */

/**
 * A skeleton, not a spinner.
 *
 * A spinner says "something is happening"; a skeleton says "this is what is arriving, and roughly
 * how much of it", which stops the layout jumping when data lands.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('rounded-md bg-surface-sunken shimmer', className)}
    />
  );
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-label="Loading results" className="divide-y divide-border-subtle">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3.5">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-4', columnIndex === 0 ? 'w-40' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div role="status" aria-label="Loading" className="space-y-6">
      <Skeleton className="h-24 w-full rounded-xl" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-xl lg:col-span-2" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );
}

export function CardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
    >
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-[7.5rem] rounded-xl" />
      ))}
    </div>
  );
}

/* ============================================================================================
   Empty
   ============================================================================================ */

/**
 * An empty state explains three things: what is empty, why it might be, and what to do next.
 * "No results" alone leaves the user guessing whether the system is broken.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-14 text-center', className)}>
      <div
        aria-hidden
        className="mb-4 grid size-12 place-items-center rounded-full border border-border-subtle bg-surface-sunken text-text-muted"
      >
        {icon ?? <Inbox className="size-5" />}
      </div>
      <h3 className="text-h3 text-text-primary">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-md text-small text-text-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ============================================================================================
   Errors
   ============================================================================================ */

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  /** What the user was trying to do, used to phrase the message. */
  context?: string;
}

/**
 * The single error surface.
 *
 * Shows a human explanation, the request id (which is what support needs to find the failure in
 * the server logs and the audit trail), and a retry only where retrying could actually help.
 */
export function ErrorState({ error, onRetry, className, context }: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;
  const status = apiError?.status ?? 500;

  const { icon, title, description } = describe(status, apiError, context);
  const canRetry = onRetry && (apiError?.isRetryable ?? true);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center rounded-xl border border-border-subtle bg-surface px-6 py-12 text-center',
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          'mb-4 grid size-12 place-items-center rounded-full border',
          status === 403 || status === 401
            ? 'border-warning-border bg-warning-subtle text-warning-text'
            : 'border-danger-border bg-danger-subtle text-danger-text',
        )}
      >
        {icon}
      </div>
      <h3 className="text-h3 text-text-primary">{title}</h3>
      <p className="mt-1.5 max-w-md text-small text-text-muted">{description}</p>

      {apiError?.requestId && (
        <p className="mt-3 text-caption text-text-muted">
          Reference:{' '}
          <span className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono">
            {apiError.requestId}
          </span>
        </p>
      )}

      {canRetry && (
        <Button
          variant="secondary"
          className="mt-5"
          onClick={onRetry}
          leadingIcon={<RefreshCw className="size-4" />}
        >
          Try again
        </Button>
      )}
    </div>
  );
}

function describe(status: number, error: ApiError | null, context?: string) {
  const subject = context ? ` ${context}` : ' this';
  switch (status) {
    case 0:
      return {
        icon: <WifiOff className="size-5" />,
        title: 'Cannot reach the server',
        description:
          error?.code === 'TIMEOUT'
            ? 'The server took too long to respond. It may be busy - try again in a moment.'
            : 'Check your network connection. Nothing was changed.',
      };
    case 401:
      return {
        icon: <Lock className="size-5" />,
        title: 'Your session has ended',
        description: 'Sign in again to continue.',
      };
    case 403:
      return {
        icon: <Lock className="size-5" />,
        title: 'You do not have access',
        description: `Your account is not permitted to view${subject}. Ask an administrator if you need it.`,
      };
    case 404:
      return {
        icon: <AlertTriangle className="size-5" />,
        title: 'Not found',
        description: `We could not find${subject}. It may have been removed, or the link may be wrong.`,
      };
    case 409:
      return {
        icon: <AlertTriangle className="size-5" />,
        title: 'This conflicts with the current state',
        description:
          error?.message ??
          'Someone else may have changed this while you were working. Reload and try again.',
      };
    case 429:
      return {
        icon: <AlertTriangle className="size-5" />,
        title: 'Too many requests',
        description: 'You have made a lot of requests in a short time. Wait a moment and retry.',
      };
    default:
      return {
        icon: <ServerCrash className="size-5" />,
        title: 'Something went wrong',
        description:
          error?.message ??
          'An unexpected error occurred. The problem has been logged with the reference below.',
      };
  }
}

/** Inline variant, for a failure inside a card rather than a whole page. */
export function InlineError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-danger-border bg-danger-subtle px-4 py-3"
    >
      <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-danger-text" />
      <div className="min-w-0 flex-1">
        <p className="text-small font-medium text-danger-text">
          {apiError?.message ?? 'Something went wrong.'}
        </p>
        {apiError?.requestId && (
          <p className="mt-0.5 font-mono text-caption text-danger-text/80">
            {apiError.requestId}
          </p>
        )}
      </div>
      {onRetry && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}
